import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ArrowLeft, Trash2, FileText, Clock, Terminal, Search, X, Pencil, Check } from 'lucide-react';
import { clientsApi, SshSession, TenantSearchResult, Deployment } from '../../api/endpoints/clients';
import { integrationsApi, Integration } from '../../api/endpoints/integrations';
import { queryClient } from '../../providers/QueryProvider';
import { useSocket } from '../../hooks/useSocket';
import { Card, StatCard } from '../../components/ui/Card';
import { StatusBadge } from '../../components/ui/Badge';
import { Table, Thead, Th, Td, Tr } from '../../components/ui/Table';
import { Pagination } from '../../components/ui/Pagination';
import { PageSpinner } from '../../components/ui/Spinner';
import { formatDate, formatDuration, formatTimeAgo } from '../../utils/formatters';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

export function ClientDetailPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const [timelineHours, setTimelineHours] = useState(24);
  const [metricsRange, setMetricsRange] = useState('');
  const [offlinePage, setOfflinePage] = useState(1);
  const eventsPerPage = 10;
  const [notes, setNotes] = useState('');
  const [notesLoaded, setNotesLoaded] = useState(false);

  // SSH session state
  const [sshDuration, setSshDuration] = useState(60);
  const [sshCountdown, setSshCountdown] = useState('');
  // Chisel state
  const [chiselDuration, setChiselDuration] = useState(30);
  const [chiselCountdown, setChiselCountdown] = useState('');
  // Rename state
  const [isRenaming, setIsRenaming] = useState(false);
  const [newName, setNewName] = useState('');

  // Tenant/Deployment search state
  const [tenantSearch, setTenantSearch] = useState('');
  const [tenantResults, setTenantResults] = useState<TenantSearchResult[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<TenantSearchResult | null>(null);
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [selectedDeployment, setSelectedDeployment] = useState<Deployment | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showTenantDropdown, setShowTenantDropdown] = useState(false);
  const [deploymentFilter, setDeploymentFilter] = useState('');

  // Integration state
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<string>('');

  // --- Queries ---
  const { data: clientData, isLoading } = useQuery({
    queryKey: ['client', clientId],
    queryFn: () => clientsApi.get(clientId!),
    enabled: !!clientId,
    refetchInterval: 30000,
    gcTime: 0,           // Don't cache — always fetch fresh on page visit
    placeholderData: undefined, // Never show stale data from previous visit
  });

  const { data: timeline } = useQuery({
    queryKey: ['timeline', clientId, timelineHours],
    queryFn: () => clientsApi.timeline(clientId!, timelineHours),
    enabled: !!clientId,
    refetchInterval: 60000,
    gcTime: 0,
  });

  const { data: metrics } = useQuery({
    queryKey: ['metrics', clientId, metricsRange],
    queryFn: () => clientsApi.metrics(clientId!, metricsRange),
    enabled: !!clientId,
  });

  const { data: sshData, refetch: refetchSsh } = useQuery({
    queryKey: ['sshSessions', clientId],
    queryFn: () => clientsApi.sshSessions(clientId!),
    enabled: !!clientId,
    // Poll faster (3s) while a session is pending to catch activation quickly
    refetchInterval: (query) => {
      const sessions = query.state.data?.sessions;
      const hasPending = sessions?.some((s: SshSession) => s.status === 'pending');
      return hasPending ? 3000 : 15000;
    },
  });

  // TRIAL: Chisel tunnel status
  const { data: chiselData, refetch: refetchChisel } = useQuery({
    queryKey: ['chiselStatus', clientId],
    queryFn: () => clientsApi.chiselStatus(clientId!),
    enabled: !!clientId && !!clientData?.client?.chisel_enabled,
    refetchInterval: 10000,
  });

  const { data: integrationsData } = useQuery({
    queryKey: ['integrations'],
    queryFn: () => integrationsApi.list(),
  });

  // --- Mutations ---
  const deleteMutation = useMutation({
    mutationFn: () => clientsApi.delete(clientId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      navigate('/clients');
    },
  });

  const notesMutation = useMutation({
    mutationFn: (text: string) => clientsApi.updateNotes(clientId!, text),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client', clientId] });
    },
  });

  const createSshMutation = useMutation({
    mutationFn: () => clientsApi.createSshSession(clientId!, sshDuration),
    onSuccess: () => {
      refetchSsh();
    },
    onError: (err: any) => {
      // If session already exists — just refetch to show it
      if (err?.message?.includes('already exists')) {
        refetchSsh();
      }
    },
  });

  const revokeSshMutation = useMutation({
    mutationFn: (sessionId: string) => clientsApi.revokeSshSession(sessionId),
    onSuccess: () => {
      refetchSsh();
    },
  });

  const renameMutation = useMutation({
    mutationFn: (name: string) => clientsApi.rename(clientId!, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client', clientId] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setIsRenaming(false);
    },
  });

  const chiselToggleMutation = useMutation({
    mutationFn: (enabled: boolean) => clientsApi.toggleChisel(clientId!, enabled, chiselDuration),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client', clientId] });
      queryClient.invalidateQueries({ queryKey: ['chiselStatus', clientId] });
    },
  });

  const integrationMutation = useMutation({
    mutationFn: (integrationId: string | null) => clientsApi.updateIntegration(clientId!, integrationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client', clientId] });
    },
  });

  const deploymentMutation = useMutation({
    mutationFn: (data: { tenant_id: string | null; tenant_name: string | null; deployment_id: string | null; deployment_name: string | null; subdomain: string | null }) =>
      clientsApi.updateDeploymentFull(clientId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client', clientId] });
      setSelectedTenant(null);
      setSelectedDeployment(null);
      setDeployments([]);
      setTenantSearch('');
    },
  });

  // --- Socket for real-time SSH session updates ---
  useSocket('sshSessionUpdate', (data: { client_id: string }) => {
    if (data.client_id === clientId) {
      refetchSsh();
    }
  });

  // --- Effects ---
  const client = clientData?.client;

  // Load notes from client
  useEffect(() => {
    if (client && !notesLoaded) {
      setNotes(client.notes || '');
      setNotesLoaded(true);
    }
  }, [client, notesLoaded]);

  // Load selected integration from client
  useEffect(() => {
    if (client) {
      setSelectedIntegrationId(client.integration_id || '');
    }
  }, [client]);

  // SSH countdown timer
  const activeSession = sshData?.sessions?.find(
    (s: SshSession) => s.status === 'active' || s.status === 'pending'
  );

  useEffect(() => {
    if (!activeSession) {
      setSshCountdown('');
      return;
    }
    const updateCountdown = () => {
      const remaining = new Date(activeSession.expires_at).getTime() - Date.now();
      if (remaining <= 0) {
        setSshCountdown('Expired');
        refetchSsh();
        return;
      }
      const m = Math.floor(remaining / 60000);
      const s = Math.floor((remaining % 60000) / 1000);
      setSshCountdown(`${m}m ${s}s`);
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [activeSession, refetchSsh]);

  // Chisel countdown timer
  const chiselExpiry = client?.chisel_expires_at || chiselData?.chisel_expires_at;
  useEffect(() => {
    if (!client?.chisel_enabled || !chiselExpiry) {
      setChiselCountdown('');
      return;
    }
    const updateCountdown = () => {
      const remaining = new Date(chiselExpiry).getTime() - Date.now();
      if (remaining <= 0) {
        setChiselCountdown('Expired');
        queryClient.invalidateQueries({ queryKey: ['client', clientId] });
        return;
      }
      const m = Math.floor(remaining / 60000);
      const s = Math.floor((remaining % 60000) / 1000);
      setChiselCountdown(`${m}m ${s}s`);
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [client?.chisel_enabled, chiselExpiry, clientId, queryClient]);

  // --- Tenant search ---
  const doTenantSearch = useCallback(async (query: string) => {
    if (query.length < 2) {
      setTenantResults([]);
      setShowTenantDropdown(false);
      return;
    }
    setSearchLoading(true);
    try {
      const res = await clientsApi.searchTenants(query);
      setTenantResults(res.tenants || []);
      setShowTenantDropdown(true);
    } catch {
      setTenantResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (tenantSearch.length >= 2) {
        doTenantSearch(tenantSearch);
      } else {
        setTenantResults([]);
        setShowTenantDropdown(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [tenantSearch, doTenantSearch]);

  // Load deployments when tenant is selected
  const loadDeployments = useCallback(async (tenant: TenantSearchResult) => {
    setSelectedTenant(tenant);
    setShowTenantDropdown(false);
    setSelectedDeployment(null);
    setDeploymentFilter('');
    const tid = tenant.tenant_id || tenant._id || tenant.id || '';
    try {
      const res = await clientsApi.getDeployments(tid);
      setDeployments(res.deployments || []);
    } catch {
      setDeployments([]);
    }
  }, []);

  // --- Render ---
  if (isLoading) return <PageSpinner />;

  if (!client) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-600 dark:text-gray-400">Client not found</p>
        <Link to="/clients" className="text-purple-600 text-sm mt-2 inline-block">Back to clients</Link>
      </div>
    );
  }

  const analytics = timeline?.analytics;
  const metricsHistory = metrics?.metrics || [];
  const integrations: Integration[] = integrationsData?.integrations || [];

  const tunnelStatus = client.metrics?.tunnel_status || '-';
  const tunnelBg = tunnelStatus === 'active' || tunnelStatus === 'connected'
    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
    : tunnelStatus === 'degraded'
      ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
      : 'bg-slate-200 dark:bg-gray-800 text-slate-700 dark:text-gray-400';

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-slate-600 hover:text-slate-900 dark:hover:text-gray-200">
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              {isRenaming ? (
                <form onSubmit={e => { e.preventDefault(); if (newName.trim()) renameMutation.mutate(newName.trim()); }} className="flex items-center gap-1">
                  <input
                    type="text"
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    autoFocus
                    className="text-sm font-bold border border-purple-300 dark:border-purple-600 rounded px-2 py-0.5 dark:bg-gray-700 dark:text-white"
                    onKeyDown={e => e.key === 'Escape' && setIsRenaming(false)}
                  />
                  <button type="submit" disabled={renameMutation.isPending} className="text-green-600 hover:text-green-800">
                    <Check size={16} />
                  </button>
                  <button type="button" onClick={() => setIsRenaming(false)} className="text-slate-400 hover:text-slate-600">
                    <X size={16} />
                  </button>
                </form>
              ) : (
                <>
                  <h1 className="text-sm font-bold text-gray-900 dark:text-white">
                    {client.deployment_name || client.client_name || client.client_id}
                  </h1>
                  <button
                    onClick={() => { setNewName(client.client_name || ''); setIsRenaming(true); }}
                    className="text-slate-400 hover:text-purple-600"
                    title="Rename device"
                  >
                    <Pencil size={14} />
                  </button>
                </>
              )}
              <StatusBadge status={client.status} />
            </div>
            <p className="text-xs text-slate-600 dark:text-gray-400">{client.client_id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/clients/${client.client_id}/report`}
            target="_blank"
            className="px-3 py-1.5 border border-slate-300 dark:border-gray-600 rounded-md text-xs text-slate-800 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-700 flex items-center gap-1"
          >
            <FileText size={14} />
            Report
          </Link>
          <button
            onClick={() => { if (confirm('Delete this client?')) deleteMutation.mutate(); }}
            className="px-3 py-1.5 bg-red-600 text-white rounded-md text-xs hover:bg-red-700 flex items-center gap-1"
          >
            <Trash2 size={14} />
            Delete
          </button>
        </div>
      </div>

      {/* Uptime Overview */}
      {analytics && (
        <Card title="Uptime Overview" className="mb-4" action={
          <select value={timelineHours} onChange={e => { setTimelineHours(Number(e.target.value)); setOfflinePage(1); }} className="text-xs border border-slate-300 dark:border-gray-600 rounded px-2 py-1 dark:bg-gray-700 dark:text-white">
            <option value={6}>6h</option>
            <option value={24}>24h</option>
            <option value={48}>48h</option>
            <option value={168}>7d</option>
            <option value={720}>30d</option>
          </select>
        }>
          <div className="p-4">
            <div className="grid grid-cols-5 gap-4 mb-4">
              <StatCard label="Uptime" value={`${analytics.uptime_percent.toFixed(1)}%`} color="green" />
              <StatCard label="Downtime" value={formatDuration(analytics.total_downtime_ms)} color="red" />
              <StatCard label="Disconnections" value={analytics.disconnect_count} color="yellow" />
              <StatCard label="Avg Down" value={formatDuration(analytics.avg_downtime_ms)} color="gray" />
              <StatCard label="Longest Down" value={formatDuration(analytics.longest_downtime_ms)} color="gray" />
            </div>

            {/* Timeline bar */}
            <TimelineBar changes={timeline?.changes || []} period={timeline?.period} />

            {/* Timeline legends */}
            <div className="flex items-center gap-4 mt-2">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm bg-emerald-500" />
                <span className="text-[10px] text-slate-600 dark:text-gray-400">Online</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm bg-rose-600" />
                <span className="text-[10px] text-slate-600 dark:text-gray-400">Offline</span>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* 2-column: Resource Chart (60%) + Health (40%) */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-4">
        <Card title="Resource Usage" className="lg:col-span-3" action={
          <select value={metricsRange} onChange={e => setMetricsRange(e.target.value)} className="text-xs border border-slate-300 dark:border-gray-600 rounded px-2 py-1 dark:bg-gray-700 dark:text-white">
            <option value="">~30 min</option>
            <option value="6h">6h</option>
            <option value="24h">24h</option>
            <option value="7d">7d</option>
            <option value="30d">30d</option>
          </select>
        }>
          <div className="p-4 h-64">
            {metricsHistory.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={metricsHistory}>
                  <CartesianGrid stroke="none" />
                  <XAxis dataKey="timestamp" tickFormatter={t => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} tick={{ fontSize: 10 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: 11 }} labelFormatter={t => new Date(t).toLocaleString()} />
                  <Line type="monotone" dataKey="cpu_percent" stroke="#ef4444" name="CPU" dot={false} strokeWidth={1.5} />
                  <Line type="monotone" dataKey="memory_percent" stroke="#3b82f6" name="Memory" dot={false} strokeWidth={1.5} />
                  <Line type="monotone" dataKey="disk_percent" stroke="#f59e0b" name="Disk" dot={false} strokeWidth={1.5} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-sm text-slate-500">No metrics data</div>
            )}
          </div>
        </Card>

        <Card title="Health" className="lg:col-span-2" action={
          client.metrics?.collected_at
            ? <span className={`text-xs ${client.status === 'connected' ? 'text-slate-600' : 'text-red-500'}`}>{client.status === 'connected' ? new Date(client.metrics.collected_at).toLocaleTimeString() : formatTimeAgo(client.metrics.collected_at)}</span>
            : undefined
        }>
          <div className="p-4">
            {!client.metrics || !client.metrics.collected_at ? (
              <div className="text-center py-4 text-xs text-slate-500">No monitoring data yet</div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div className={`rounded-lg p-2 ${tunnelBg}`}>
                    <p className="text-xs text-slate-600">Tunnel</p>
                    <p className="text-sm font-bold">{tunnelStatus}</p>
                  </div>
                  <div className="rounded-lg p-2 bg-blue-50 dark:bg-blue-900/20">
                    <p className="text-xs text-slate-600">Latency</p>
                    <p className={`text-sm font-bold ${(client.metrics.latency_ms || 0) > 200 ? 'text-red-600' : (client.metrics.latency_ms || 0) > 100 ? 'text-yellow-600' : 'text-green-600'}`}>
                      {client.metrics.latency_ms != null ? `${client.metrics.latency_ms}ms` : 'N/A'}
                    </p>
                  </div>
                  <div className="rounded-lg p-2 bg-purple-50 dark:bg-purple-900/20">
                    <p className="text-xs text-slate-600">Uptime</p>
                    <p className="text-sm font-bold text-purple-700 dark:text-purple-400">
                      {client.metrics.uptime_seconds ? formatDuration(client.metrics.uptime_seconds * 1000) : 'N/A'}
                    </p>
                  </div>
                  <div className="rounded-lg p-2 bg-slate-100 dark:bg-gray-900/50">
                    <p className="text-xs text-slate-600">Reconnects</p>
                    <p className="text-sm font-bold text-slate-800 dark:text-gray-300">{client.metrics.reconnect_count || 0}</p>
                  </div>
                  <div className={`rounded-lg p-2 ${client.metrics.mongodb_status === 'up' ? 'bg-green-50 dark:bg-green-900/20' : client.metrics.mongodb_status === 'down' ? 'bg-red-50 dark:bg-red-900/20' : 'bg-slate-100 dark:bg-gray-900/50'}`}>
                    <p className="text-xs text-slate-600">MongoDB</p>
                    <p className={`text-sm font-bold ${client.metrics.mongodb_status === 'up' ? 'text-green-600' : client.metrics.mongodb_status === 'down' ? 'text-red-600' : 'text-slate-500'}`}>
                      {client.metrics.mongodb_status || '-'}
                    </p>
                  </div>
                  <div className={`rounded-lg p-2 ${client.metrics.pm2_status?.includes('online') ? 'bg-green-50 dark:bg-green-900/20' : client.metrics.pm2_status?.includes('errored') ? 'bg-red-50 dark:bg-red-900/20' : 'bg-slate-100 dark:bg-gray-900/50'}`}>
                    <p className="text-xs text-slate-600">PM2</p>
                    <p className={`text-xs font-bold ${client.metrics.pm2_status?.includes('errored') ? 'text-red-600' : client.metrics.pm2_status?.includes('online') ? 'text-green-600' : 'text-slate-500'}`}>
                      {client.metrics.pm2_status || '-'}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div>
                    <div className="flex justify-between text-xs"><span className="text-slate-700">CPU</span><span className="font-semibold">{client.metrics.cpu_percent != null ? `${client.metrics.cpu_percent}%` : 'N/A'}</span></div>
                    {client.metrics.cpu_percent != null && (
                      <div className="w-full bg-slate-200 dark:bg-gray-700 rounded-full h-1.5 mt-1">
                        <div className={`h-1.5 rounded-full ${(client.metrics.cpu_percent) > 90 ? 'bg-red-500' : (client.metrics.cpu_percent) > 70 ? 'bg-yellow-500' : 'bg-red-400'}`} style={{ width: `${Math.min(client.metrics.cpu_percent, 100)}%` }} />
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="flex justify-between text-xs"><span className="text-slate-700">Memory</span><span className="font-semibold">{client.metrics.memory_percent != null ? `${client.metrics.memory_percent}%` : 'N/A'}</span></div>
                    {client.metrics.memory_percent != null && (
                      <div className="w-full bg-slate-200 dark:bg-gray-700 rounded-full h-1.5 mt-1">
                        <div className={`h-1.5 rounded-full ${(client.metrics.memory_percent) > 90 ? 'bg-red-500' : (client.metrics.memory_percent) > 70 ? 'bg-yellow-500' : 'bg-blue-400'}`} style={{ width: `${Math.min(client.metrics.memory_percent, 100)}%` }} />
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="flex justify-between text-xs"><span className="text-slate-700">Disk</span><span className="font-semibold">{client.metrics.disk_percent != null ? `${client.metrics.disk_percent}%` : 'N/A'}</span></div>
                    {client.metrics.disk_percent != null && (
                      <div className="w-full bg-slate-200 dark:bg-gray-700 rounded-full h-1.5 mt-1">
                        <div className={`h-1.5 rounded-full ${(client.metrics.disk_percent) > 95 ? 'bg-red-500' : (client.metrics.disk_percent) > 80 ? 'bg-yellow-500' : 'bg-amber-400'}`} style={{ width: `${Math.min(client.metrics.disk_percent, 100)}%` }} />
                      </div>
                    )}
                  </div>
                </div>
                {client.metrics.last_error && client.metrics.last_error !== '' && client.metrics.last_error !== 'None' && (
                  <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs text-red-600 dark:text-red-400 font-mono">
                    {client.metrics.last_error}
                  </div>
                )}
                {client.metrics.mongodb_errors && client.metrics.mongodb_errors !== '' && (
                  <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded">
                    <p className="text-[10px] font-semibold text-red-700 dark:text-red-300 mb-1">MongoDB Error Logs</p>
                    <pre className="text-[10px] text-red-600 dark:text-red-400 font-mono whitespace-pre-wrap break-all">
                      {client.metrics.mongodb_errors.split('|').filter(Boolean).join('\n')}
                    </pre>
                  </div>
                )}
                {client.metrics.pm2_errors && client.metrics.pm2_errors !== '' && (
                  <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded">
                    <p className="text-[10px] font-semibold text-red-700 dark:text-red-300 mb-1">PM2 Error Logs</p>
                    <pre className="text-[10px] text-red-600 dark:text-red-400 font-mono whitespace-pre-wrap break-all">
                      {client.metrics.pm2_errors.split('|').filter(Boolean).join('\n')}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Offline Events */}
      <OfflineEventsTable events={analytics?.offline_events || []} page={offlinePage} perPage={eventsPerPage} onPageChange={setOfflinePage} />

      {/* Client Details Grid - 8 columns matching EJS */}
      <Card title="Details" className="mb-4">
        <div className="p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
          {([
            ['Client ID', client.client_id],
            ['Hostname', client.hostname || '-'],
            ['Local IP', client.local_ip || '-'],
            ['Public IP', client.public_ip || '-'],
            ['Additional Ports', client.additional_ports?.length ? client.additional_ports.join(', ') : '-'],
            ['Registered', formatDate(client.createdAt)],
            ['Last Seen', client.status === 'connected'
              ? (client.connected_at ? `Online since ${formatTimeAgo(client.connected_at)}` : formatTimeAgo(client.last_seen))
              : client.last_seen ? `Offline since ${formatTimeAgo(client.last_seen)}` : 'Never'],
          ] as [string, string | number][]).map(([label, value]) => (
            <div key={label as string}>
              <p className="text-[10px] text-slate-500 dark:text-gray-400 uppercase">{label}</p>
              <p className="text-xs font-medium text-slate-900 dark:text-gray-200 break-all">{String(value)}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* SSH Access (70%) + Tenant & Deployment (30%) */}
      <div className="grid grid-cols-1 lg:grid-cols-10 gap-4 mb-4">
        {/* SSH Access */}
        <Card title="SSH Access" className="lg:col-span-7">
          <div className="p-4">
            {/* TRIAL: Chisel HTTP tunnel */}
            {client.chisel_enabled ? (() => {
              const tunnelUp = chiselData?.tunnel_active ?? false;
              const statusKnown = !!chiselData;
              return (
                <div className={`${tunnelUp ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'} border rounded-lg p-4`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Terminal size={16} className={tunnelUp ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'} />
                      <span className={`text-sm font-semibold ${tunnelUp ? 'text-green-700 dark:text-green-400' : 'text-blue-700 dark:text-blue-400'}`}>
                        {tunnelUp ? 'SSH Access Available' : statusKnown ? 'Chisel Tunnel Offline' : 'Checking tunnel...'}
                      </span>
                      {tunnelUp && <span className="inline-block w-2 h-2 bg-green-500 rounded-full animate-pulse" />}
                    </div>
                    {chiselCountdown && (
                      <span className={`text-xs font-mono px-2 py-1 rounded ${chiselCountdown === 'Expired' ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300' : 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300'}`}>
                        <Clock size={12} className="inline mr-1" />
                        {chiselCountdown}
                      </span>
                    )}
                  </div>

                  <div className="bg-gray-900 rounded p-3 mb-3">
                    <code className="text-green-400 text-xs font-mono select-all">
                      ssh -p {client.port} posist@{window.location.hostname}
                    </code>
                    {!tunnelUp && statusKnown && (
                      <p className="text-blue-400/60 text-[10px] font-mono mt-1">Tunnel not active — client may need chisel patch deployed</p>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-600 dark:text-gray-400">
                      Port: {client.port} | HTTP Tunnel | {client.chisel_expires_at ? `Expires: ${formatDate(client.chisel_expires_at)}` : ''}
                    </span>
                    <button
                      onClick={() => { if (confirm('Disable Chisel HTTP tunnel for this client?')) chiselToggleMutation.mutate(false); }}
                      disabled={chiselToggleMutation.isPending}
                      className="px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 disabled:opacity-50"
                    >
                      {chiselToggleMutation.isPending ? 'Disabling...' : 'Disable'}
                    </button>
                  </div>
                </div>
              );
            })() : activeSession ? (() => {
              const isPending = activeSession.status === 'pending';
              const borderColor = isPending
                ? 'border-yellow-200 dark:border-yellow-800'
                : 'border-green-200 dark:border-green-800';
              const bgColor = isPending
                ? 'bg-yellow-50 dark:bg-yellow-900/20'
                : 'bg-green-50 dark:bg-green-900/20';
              const badgeBg = isPending
                ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300'
                : 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300';
              return (
                <div className={`${bgColor} border ${borderColor} rounded-lg p-4`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {isPending ? (
                        <svg className="animate-spin h-4 w-4 text-yellow-600 dark:text-yellow-400" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      ) : (
                        <Terminal size={16} className="text-green-600 dark:text-green-400" />
                      )}
                      <span className={`text-sm font-semibold ${isPending ? 'text-yellow-700 dark:text-yellow-400' : 'text-green-700 dark:text-green-400'}`}>
                        {isPending ? 'Connecting to device...' : 'SSH Session Active'}
                      </span>
                    </div>
                    <span className={`text-xs font-mono ${badgeBg} px-2 py-1 rounded`}>
                      <Clock size={12} className="inline mr-1" />
                      {sshCountdown}
                    </span>
                  </div>

                  <div className="bg-gray-900 rounded p-3 mb-3">
                    {isPending ? (
                      <>
                        <p className="text-yellow-400 text-xs font-mono">Waiting for client to open reverse tunnel…</p>
                        <p className="text-yellow-500/60 text-[10px] font-mono mt-1">Usually takes 10-40s. The SSH command will appear once the tunnel is ready.</p>
                      </>
                    ) : (
                      <code className="text-green-400 text-xs font-mono select-all">
                        ssh -p {activeSession.port} posist@{window.location.hostname}
                      </code>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-600 dark:text-gray-400">
                      {isPending ? 'Status: connecting' : `Port: ${activeSession.port} | Expires: ${formatDate(activeSession.expires_at)}`}
                    </span>
                    <button
                      onClick={() => revokeSshMutation.mutate(activeSession.session_id)}
                      disabled={revokeSshMutation.isPending}
                      className="px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 disabled:opacity-50"
                    >
                      {isPending ? 'Cancel' : 'Revoke'}
                    </button>
                  </div>
                </div>
              );
            })() : (
              <div className="flex items-center gap-4">
                <select
                  value={sshDuration}
                  onChange={e => setSshDuration(Number(e.target.value))}
                  className="text-xs border border-slate-300 dark:border-gray-600 rounded px-2 py-1.5 dark:bg-gray-700 dark:text-white"
                >
                  <option value={15}>15 min</option>
                  <option value={30}>30 min</option>
                  <option value={60}>60 min</option>
                  <option value={120}>120 min</option>
                </select>
                <button
                  onClick={() => createSshMutation.mutate()}
                  disabled={createSshMutation.isPending || !!activeSession || client.status !== 'connected'}
                  className="px-4 py-1.5 bg-purple-600 text-white rounded text-xs hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1"
                >
                  <Terminal size={14} />
                  {createSshMutation.isPending ? 'Connecting...' : 'Connect SSH'}
                </button>
                <select
                  value={chiselDuration}
                  onChange={e => setChiselDuration(Number(e.target.value))}
                  className="text-xs border border-slate-300 dark:border-gray-600 rounded px-2 py-1.5 dark:bg-gray-700 dark:text-white"
                >
                  <option value={15}>15 min</option>
                  <option value={30}>30 min</option>
                  <option value={60}>60 min</option>
                  <option value={120}>120 min</option>
                </select>
                <button
                  onClick={() => chiselToggleMutation.mutate(true)}
                  disabled={chiselToggleMutation.isPending}
                  className="px-3 py-1.5 bg-orange-600 text-white rounded text-xs hover:bg-orange-700 disabled:opacity-50 flex items-center gap-1"
                  title="For clients behind aggressive firewalls that kill SSH tunnels"
                >
                  Chisel Tunnel
                </button>
                {createSshMutation.isError && !activeSession && (
                  <span className="text-xs text-red-500">Failed to create session</span>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Tenant & Deployment */}
        <Card title="Tenant & Deployment" className="lg:col-span-3">
          <div className="p-4 space-y-3 overflow-hidden">
            {/* Current mapping */}
            {client.deployment_name && (
              <div className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg min-w-0">
                <span className="text-xs font-medium text-blue-800 dark:text-blue-300 truncate mr-2">{client.deployment_name}</span>
                <button
                  onClick={() => { if (confirm('Clear deployment mapping?')) deploymentMutation.mutate({ tenant_id: null, tenant_name: null, deployment_id: null, deployment_name: null, subdomain: null }); }}
                  className="text-red-600 hover:text-red-800 text-xs font-medium shrink-0"
                >
                  Clear
                </button>
              </div>
            )}

            {/* Tenant search */}
            {!selectedTenant ? (
              <div>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={tenantSearch}
                    onChange={e => setTenantSearch(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') doTenantSearch(tenantSearch); }}
                    placeholder="Search by subdomain..."
                    className="min-w-0 flex-1 px-2 py-1.5 border border-slate-300 dark:border-gray-600 rounded-md text-xs focus:ring-1 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
                  />
                  <button
                    onClick={() => doTenantSearch(tenantSearch)}
                    className="bg-purple-600 text-white px-3 py-1.5 rounded-md hover:bg-purple-700 text-xs font-medium shrink-0"
                  >
                    Search
                  </button>
                </div>
                {searchLoading && <div className="text-center py-2 text-xs text-slate-500">Loading...</div>}
                {showTenantDropdown && tenantResults.length > 0 && (
                  <div className="mt-2 border border-slate-200 dark:border-gray-700 rounded-md max-h-40 overflow-y-auto">
                    {tenantResults.map((t, i) => (
                      <div
                        key={i}
                        className="px-2 py-1.5 hover:bg-purple-50 dark:hover:bg-purple-900/20 cursor-pointer text-xs border-b border-slate-100 dark:border-gray-700 last:border-0 transition-colors truncate"
                        onClick={() => {
                          loadDeployments(t);
                          setTenantSearch(t.name || t.tenant_name || '');
                        }}
                      >
                        <span className="font-medium text-purple-700 dark:text-purple-400 font-mono">{t.subdomain || ''}</span>
                        {' '}
                        <span className="text-slate-600">{t.name || t.tenant_name || ''}</span>
                      </div>
                    ))}
                  </div>
                )}
                {showTenantDropdown && tenantResults.length === 0 && !searchLoading && tenantSearch.length >= 2 && (
                  <div className="text-xs text-slate-500 py-1 mt-1">No tenants found</div>
                )}
              </div>
            ) : (
              /* Selected tenant + deployment selector */
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg min-w-0">
                  <div className="min-w-0 truncate mr-2">
                    <span className="text-xs font-medium text-purple-700 dark:text-purple-400 font-mono">{selectedTenant.subdomain || ''}</span>
                    {' '}
                    <span className="text-xs text-slate-600">{selectedTenant.name || selectedTenant.tenant_name || ''}</span>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedTenant(null);
                      setDeployments([]);
                      setSelectedDeployment(null);
                      setTenantSearch('');
                      setShowTenantDropdown(false);
                    }}
                    className="text-slate-500 hover:text-slate-800 dark:hover:text-gray-200 shrink-0"
                    title="Cancel selection"
                  >
                    <X size={14} />
                  </button>
                </div>

                {deployments.length > 0 ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={deploymentFilter}
                      onChange={e => setDeploymentFilter(e.target.value)}
                      placeholder="Filter deployments..."
                      className="w-full px-2 py-1.5 border border-slate-300 dark:border-gray-600 rounded-md text-xs focus:ring-1 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
                    />
                    <div className="max-h-32 overflow-y-auto border border-slate-200 dark:border-gray-700 rounded-md">
                      {deployments
                        .filter(d => {
                          if (!deploymentFilter) return true;
                          const label = (d.deployment_name || d.name || d.deployment_id || '').toLowerCase();
                          return label.includes(deploymentFilter.toLowerCase());
                        })
                        .map((d, i) => {
                          const depId = d.deployment_id || d._id || d.id || '';
                          const selId = selectedDeployment?.deployment_id || selectedDeployment?._id || '';
                          const isSelected = depId === selId;
                          return (
                            <div
                              key={i}
                              className={`px-2 py-1.5 cursor-pointer text-xs border-b border-slate-100 dark:border-gray-700 last:border-0 transition-colors truncate ${
                                isSelected
                                  ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 font-medium'
                                  : 'hover:bg-purple-50 dark:hover:bg-purple-900/20 text-slate-700 dark:text-gray-300'
                              }`}
                              onClick={() => setSelectedDeployment(d)}
                            >
                              {d.deployment_name || d.name || d.deployment_id || 'Unknown'}
                            </div>
                          );
                        })}
                    </div>
                    <button
                      onClick={() => {
                        if (!selectedDeployment || !selectedTenant) return;
                        deploymentMutation.mutate({
                          tenant_id: selectedTenant.tenant_id || selectedTenant._id || selectedTenant.id || null,
                          tenant_name: selectedTenant.tenant_name || selectedTenant.name || null,
                          deployment_id: selectedDeployment.deployment_id || selectedDeployment._id || selectedDeployment.id || null,
                          deployment_name: selectedDeployment.deployment_name || selectedDeployment.name || null,
                          subdomain: selectedTenant.subdomain || null,
                        });
                      }}
                      disabled={!selectedDeployment || deploymentMutation.isPending}
                      className="w-full bg-purple-600 text-white px-3 py-1.5 rounded-md hover:bg-purple-700 text-xs font-medium disabled:opacity-50"
                    >
                      {deploymentMutation.isPending ? 'Updating...' : 'Update'}
                    </button>
                  </div>
                ) : (
                  <div className="text-xs text-slate-500 py-1">No deployments found for this tenant.</div>
                )}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Integration (50%) + Notes (50%) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* Integration */}
        <Card title="Integration">
          <div className="p-4 space-y-3">
            {client.integration_id && (
              <p className="text-xs text-slate-600 dark:text-gray-400">
                Currently linked: <span className="font-medium text-slate-900 dark:text-gray-200">
                  {integrations.find(i => i._id === client.integration_id)?.name || client.integration_id}
                </span>
              </p>
            )}
            <div className="flex items-center gap-2">
              <select
                value={selectedIntegrationId}
                onChange={e => setSelectedIntegrationId(e.target.value)}
                className="flex-1 text-xs border border-slate-300 dark:border-gray-600 rounded px-2 py-1.5 dark:bg-gray-700 dark:text-white"
              >
                <option value="">None</option>
                {integrations.map(integ => (
                  <option key={integ._id} value={integ._id}>{integ.name}</option>
                ))}
              </select>
              <button
                onClick={() => integrationMutation.mutate(selectedIntegrationId || null)}
                disabled={integrationMutation.isPending}
                className="px-3 py-1.5 bg-purple-600 text-white rounded text-xs hover:bg-purple-700 disabled:opacity-50"
              >
                {integrationMutation.isPending ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </Card>

        {/* Notes */}
        <Card title="Notes">
          <div className="p-4 space-y-3">
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              className="w-full text-xs border border-slate-300 dark:border-gray-600 rounded px-3 py-2 dark:bg-gray-700 dark:text-white resize-none"
              placeholder="Add notes about this client..."
            />
            <button
              onClick={() => notesMutation.mutate(notes)}
              disabled={notesMutation.isPending}
              className="px-3 py-1.5 bg-purple-600 text-white rounded text-xs hover:bg-purple-700 disabled:opacity-50"
            >
              {notesMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
}

// Timeline bar component
function TimelineBar({ changes, period }: { changes: Array<{ status: string; timestamp: string }>; period?: { since: string; until: string } }) {
  if (!period) return null;
  const start = new Date(period.since).getTime();
  const end = new Date(period.until).getTime();
  const total = end - start;
  if (total <= 0) return null;

  const segments: Array<{ left: number; width: number; online: boolean }> = [];
  let lastTime = start;
  let lastOnline = true;

  for (const change of changes) {
    const t = new Date(change.timestamp).getTime();
    if (t > lastTime && t <= end) {
      segments.push({
        left: ((lastTime - start) / total) * 100,
        width: ((t - lastTime) / total) * 100,
        online: lastOnline,
      });
      lastTime = t;
    }
    lastOnline = change.status === 'connected';
  }
  // Final segment
  segments.push({
    left: ((lastTime - start) / total) * 100,
    width: ((end - lastTime) / total) * 100,
    online: lastOnline,
  });

  return (
    <div>
      <div className="relative h-6 rounded-md overflow-hidden bg-slate-200 dark:bg-gray-700">
        {segments.map((seg, i) => (
          <div
            key={i}
            className={seg.online ? 'bg-emerald-500' : 'bg-rose-600'}
            style={{ position: 'absolute', left: `${seg.left}%`, width: `${seg.width}%`, height: '100%' }}
          />
        ))}
      </div>
      <div className="flex justify-between mt-1">
        <span className="text-[10px] text-slate-600">{new Date(period.since).toLocaleString()}</span>
        <span className="text-[10px] text-slate-600">{new Date(period.until).toLocaleString()}</span>
      </div>
    </div>
  );
}

// Offline Events table with client-side pagination (data comes from timeline API)
function OfflineEventsTable({ events, page, perPage, onPageChange }: {
  events: Array<{ start: string; end: string | null; duration_ms: number }>;
  page: number;
  perPage: number;
  onPageChange: (p: number) => void;
}) {
  const reversed = [...events].reverse();
  const totalPages = Math.ceil(reversed.length / perPage);
  const startIdx = (page - 1) * perPage;
  const pageEvents = reversed.slice(startIdx, startIdx + perPage);

  return (
    <Card title="Offline Events" className="mb-4" action={
      <span className="text-xs text-slate-600">{reversed.length} event{reversed.length !== 1 ? 's' : ''}</span>
    }>
      <Table>
        <Thead>
          <tr>
            <Th>Went Offline</Th>
            <Th>Came Online</Th>
            <Th>Duration</Th>
          </tr>
        </Thead>
        <tbody className="divide-y divide-slate-200 dark:divide-gray-800">
          {pageEvents.length === 0 ? (
            <tr>
              <td colSpan={3} className="px-3 py-2 text-center text-xs text-slate-500">No offline events in this period</td>
            </tr>
          ) : (
            pageEvents.map((evt, i) => {
              const durColor = evt.duration_ms > 1800000 ? 'text-red-600 font-semibold' : evt.duration_ms > 300000 ? 'text-yellow-600 font-semibold' : '';
              return (
                <Tr key={i}>
                  <Td><span className="font-mono text-xs text-slate-800 dark:text-gray-400">{formatDate(evt.start)}</span></Td>
                  <Td>
                    {evt.end ? (
                      <span className="font-mono text-xs text-slate-800 dark:text-gray-400">{formatDate(evt.end)}</span>
                    ) : (
                      <span className="text-red-500 text-xs font-medium">Still offline</span>
                    )}
                  </Td>
                  <Td><span className={`text-xs ${durColor}`}>{formatDuration(evt.duration_ms)}</span></Td>
                </Tr>
              );
            })
          )}
        </tbody>
      </Table>
      {totalPages > 1 && (
        <div className="px-4 pb-3">
          <Pagination page={page} totalPages={totalPages} onPageChange={onPageChange} />
        </div>
      )}
    </Card>
  );
}
